import { type RefObject, useLayoutEffect } from 'react';

import {
  DEFAULT_SPREAD_END,
  isTrackSlot,
  type PoseTrack,
  poseToTransform,
  type StagePhases,
  scrollProgress,
  stagePhases,
  trackPose,
} from './sticky-scroll';

/** 연출은 카드가 가로로 나란히 설 수 있는 폭에서만 켠다 — 좁으면 세로·격자로 쌓는다 */
const STAGE_QUERY = '(min-width: 1024px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * 요소가 읽는 CSS 변수. Tailwind 쪽은 `lg:[transform:var(--scroll-transform)]` 로 받는다.
 * 카드는 `data-scroll-card="{자리 이름}"`, 함께 흐를 장식은 `data-scroll-line` 으로 표시한다.
 */
export const SCROLL_TRANSFORM_VAR = '--scroll-transform';

export type StickyScrollOptions<Slot extends string> = {
  /** 고정 프레임이 붙는 선(px) — 보통 SiteHeader 높이 */
  readonly stickyTop: number;
  readonly tracks: Readonly<Record<Slot, PoseTrack>>;
  readonly spreadEnd?: number;
  /** `[data-scroll-line]` 요소의 transform. 없으면 그런 요소는 움직이지 않는다 */
  readonly lineTransform?: (index: number, phases: StagePhases) => string;
};

/**
 * 스크롤 진행도를 카드의 transform 으로 옮긴다.
 *
 * React 상태를 거치지 않는다 — 스크롤마다 리렌더하면 프레임이 밀린다.
 * rAF 로 한 프레임에 한 번만 계산하고, 요소에는 CSS 변수만 쓴다.
 * 변수를 읽는 규칙이 `lg:` 에만 걸려 있어서, 좁은 화면에서는 값이 있어도 아무 일도 없다.
 *
 * 움직임 줄이기를 켠 사람에게는 스크롤과 무관하게 펼친 자세로 멈춰 보여준다.
 * 첫 계산은 페인트 전에 한다(useLayoutEffect) — 모인 자세가 한 프레임 보였다가 튀면 안 된다.
 *
 * `tracks`·`lineTransform` 은 모듈 상수로 넘긴다. 렌더마다 새로 만들면 구독을 다시 건다.
 */
export function useStickyScroll<Slot extends string>(
  sectionRef: RefObject<HTMLElement | null>,
  { stickyTop, tracks, spreadEnd = DEFAULT_SPREAD_END, lineTransform }: StickyScrollOptions<Slot>,
): void {
  useLayoutEffect(() => {
    const section = sectionRef.current;
    // jsdom 등 matchMedia 가 없는 환경에서는 처음 렌더한 자세 그대로 둔다
    if (!section || typeof window.matchMedia !== 'function') return;

    const stage = window.matchMedia(STAGE_QUERY);
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    const cards = Array.from(section.querySelectorAll<HTMLElement>('[data-scroll-card]'));
    const lines = Array.from(section.querySelectorAll<HTMLElement>('[data-scroll-line]'));
    let frame = 0;

    const render = (): void => {
      frame = 0;
      if (!stage.matches) return;

      const progress = reducedMotion.matches
        ? spreadEnd
        : scrollProgress(section.getBoundingClientRect(), window.innerHeight, stickyTop);

      for (const card of cards) {
        const slot = card.dataset.scrollCard;
        if (!isTrackSlot(tracks, slot)) continue;
        card.style.setProperty(
          SCROLL_TRANSFORM_VAR,
          poseToTransform(trackPose(tracks[slot], progress, spreadEnd)),
        );
      }

      if (!lineTransform) return;
      const phases = stagePhases(progress, spreadEnd);
      lines.forEach((line, index) => {
        line.style.setProperty(SCROLL_TRANSFORM_VAR, lineTransform(index, phases));
      });
    };

    const schedule = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(render);
    };

    render();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    stage.addEventListener('change', schedule);
    reducedMotion.addEventListener('change', schedule);

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      stage.removeEventListener('change', schedule);
      reducedMotion.removeEventListener('change', schedule);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [sectionRef, stickyTop, tracks, spreadEnd, lineTransform]);
}
